import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";
import type { ConfirmDialogProps } from "./types";
import { ConfirmDialogContext } from "./ConfirmDialogContext";

export function ConfirmDialogRoot({
  open,
  onClose,
  forced = false,
  children,
}: ConfirmDialogProps) {
  const shakeControls = useAnimationControls();

  function triggerShake() {
    shakeControls.start({
      x: [0, -6, 6, -4, 4, 0],
      transition: { duration: 0.4 },
    });
  }

  function handleDismissAttempt(e: Event) {
    if (forced) {
      e.preventDefault();
      triggerShake();
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      if (forced) {
        triggerShake();
      } else {
        onClose();
      }
    }
  }

  return (
    <ConfirmDialogContext.Provider value={{ forced, onClose, shakeControls }}>
      <Dialog.Root open={open} onOpenChange={handleOpenChange}>
        <AnimatePresence>
          {open && (
            <Dialog.Portal forceMount>
              <Dialog.Overlay asChild>
                <motion.div
                  className="fixed inset-0 z-[60] bg-black/50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                />
              </Dialog.Overlay>

              <Dialog.Content
                asChild
                onPointerDownOutside={handleDismissAttempt}
                onEscapeKeyDown={handleDismissAttempt}
              >
                <motion.div
                  className="fixed inset-0 z-[60] flex items-center justify-center focus:outline-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      handleOpenChange(false);
                    }
                  }}
                >
                  <motion.div animate={shakeControls}>
                    <motion.div
                      className="w-full max-w-[440px] max-h-[80vh] rounded-[12px] bg-card shadow-xl flex flex-col overflow-hidden"
                      initial={{ y: 24, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 24, opacity: 0 }}
                      transition={{
                        type: "spring",
                        damping: 25,
                        stiffness: 300,
                      }}
                    >
                      {children}
                    </motion.div>
                  </motion.div>
                </motion.div>
              </Dialog.Content>
            </Dialog.Portal>
          )}
        </AnimatePresence>
      </Dialog.Root>
    </ConfirmDialogContext.Provider>
  );
}
